/**
 * The chain's GRPO training settings as TrainingFields edits them, and
 * what a team's first switch-on fills in.
 */

import type { TrainingPresetChoice } from '@/api/generated/model'

/** YC's step count (run b2c8); a run also stops at its preset's epoch limit. */
export const DEFAULT_TRAINING_MAX_STEPS = 60

/**
 * What the switch says while it is on: a team that trains and has no
 * held-out tests gets every test of its latest scored night held out by
 * the chain (api pipeline_chain.py `hold_out_night`).
 */
export const HELD_OUT_NOTE =
  'Every test of this benchmark is held out to measure training. Training uses only generated tasks.'

export interface TrainingValues {
  enabled: boolean
  preset: string | null
  maxSteps: number | null
}

/** The step count as the input holds it: a whole number, or null for anything else. */
export function stepsFromInput(value: string): number | null {
  const steps = Number(value.trim())
  return value.trim() !== '' && Number.isInteger(steps) ? steps : null
}

/**
 * The presets that address every outside model route the benchmark's
 * adapter declares. A launch refuses a dataset route its preset gives no
 * address (api training_runs.py `_grpo_route_keys`), so any other preset
 * would have every run the chain starts refused.
 */
export function presetsFor(
  presets: readonly TrainingPresetChoice[],
  routes: readonly string[],
): TrainingPresetChoice[] {
  return presets.filter((preset) => routes.every((route) => preset.routes.includes(route)))
}

/** The preset picker's options, as TrainingFields lists them. */
export interface PresetChoices {
  /**
   * The chain's default preset (api `chain_default_preset`), listed first
   * as Default and proposed by a first switch-on. Null when the stack's
   * trainer does not offer it or it does not fit the benchmark: the
   * picker then lists the others and proposes nothing.
   */
  chainDefault: TrainingPresetChoice | null
  /** Every other preset that fits, in the trainer's order. */
  others: TrainingPresetChoice[]
}

export function presetChoices(
  presets: readonly TrainingPresetChoice[],
  routes: readonly string[],
  defaultPreset: string,
): PresetChoices {
  const fitting = presetsFor(presets, routes)
  return {
    chainDefault: fitting.find((preset) => preset.name === defaultPreset) ?? null,
    others: fitting.filter((preset) => preset.name !== defaultPreset),
  }
}
